import { TaskListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/task-list.response';
import { PlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-list.response';
import { GrowingStepListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/growing-step-list.response';
import { PlantThermalBatchResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-thermal-batch.response';
import type { GardenTodo } from '../@services/data/garden-todo.store';
import { LabelPipe } from '../pipes/LabelPipe';
import { stepNeedsAction } from './step-clock.util';
import { localDaysBetween, parseLocalDate } from './local-date.util';

/**
 * Jours laissés après la fin de la fenêtre d'une tâche : « dernier délai »,
 * puis le serveur l'archive. La même marge que l'expiration côté API.
 */
export const TASK_MARGIN_DAYS = 7;

/** Où se range une tâche : en cours, en dernier délai, à venir ; une tâche expirée ne s'affiche pas. */
export type TaskSection = 'current' | 'closing' | 'upcoming' | 'expired';

/** La fenêtre d'une tâche, telle que l'API l'envoie (« 2027-04-15 »). */
type TaskWindow = { startDate: Date | string; endDate: Date | string };

/** La rubrique d'une tâche, d'après sa fenêtre et la marge, comptée en jours du calendrier. */
export function taskSection(task: TaskWindow, today: Date): TaskSection {
  if (localDaysBetween(today, parseLocalDate(task.startDate)!) > 0) return 'upcoming';
  const daysAfterEnd = localDaysBetween(parseLocalDate(task.endDate)!, today);
  if (daysAfterEnd <= 0) return 'current';
  return daysAfterEnd <= TASK_MARGIN_DAYS ? 'closing' : 'expired';
}

/** La fin la plus proche d'abord, puis l'ordre du titre. */
export function byUrgency(a: TaskListResponse, b: TaskListResponse): number {
  const label = new LabelPipe();
  return (
    parseLocalDate(a.endDate)!.getTime() - parseLocalDate(b.endDate)!.getTime() ||
    label.transform(a.name).localeCompare(label.transform(b.name))
  );
}

/**
 * Les tâches à faire maintenant : celles dont la fenêtre court encore d'abord,
 * puis celles en marge (fenêtre finie), chaque groupe par fin la plus proche.
 * Une seule liste pour l'accueil et « Mes tâches ».
 */
export function openTasks(tasks: TaskListResponse[], today: Date): TaskListResponse[] {
  const inMargin = (task: TaskListResponse): boolean => taskSection(task, today) === 'closing';
  return tasks
    .filter((task) => ['current', 'closing'].includes(taskSection(task, today)))
    .sort((a, b) => Number(inMargin(a)) - Number(inMargin(b)) || byUrgency(a, b));
}

/**
 * Plante qui attend un geste : son étape manuelle est finie. Une plante « à
 * vérifier » (FS-11) n'en fait pas partie : on lui demande son stade réel,
 * pas de confirmer l'étape suivante.
 */
export function isPlantAction(plant: PlantListResponse, now: Date): boolean {
  return stepNeedsAction(plant, now) && !plant.currentGrowingStepToVerify;
}

/** L'étape qui suit dans le cycle : celle dont le numéro suit l'étape courante. */
export function nextStepOf(
  current: GrowingStepListResponse | undefined,
  steps: GrowingStepListResponse[],
): GrowingStepListResponse | undefined {
  if (!current || !steps?.length) return undefined;
  return steps.find((s) => s.stepNumber === (current.stepNumber ?? 0) + 1);
}

/** Les plantes complétées par leur progression thermique : « action attendue » suit la même horloge que la fiche. */
export function withThermalProgress(plants: PlantListResponse[], batch: PlantThermalBatchResponse | null): PlantListResponse[] {
  const thermalByPlant = new Map((batch?.items ?? []).map((item) => [item.plantUid, item.thermalProgress]));
  return plants.map((p) => (thermalByPlant.get(p.uid) ? { ...p, thermalProgress: thermalByPlant.get(p.uid)! } : p));
}

/**
 * La lune qualifie le geste quand le jour s'y prête (« Jour racine ») ; elle
 * n'en est jamais la raison.
 */
export function isLunarDayFor(lunarInfluence: string | null | undefined, todayInfluence: string | null | undefined): boolean {
  return !!lunarInfluence && lunarInfluence === todayInfluence;
}

/** Le compteur de « Mes tâches » : les tâches à faire maintenant et les plantes qui attendent un geste. */
export function todoCount(todo: GardenTodo, today: Date): number {
  return openTasks(todo.tasks, today).length + todo.plantActions.length;
}
